import React, { useCallback, useEffect, useMemo } from 'react'

// This is going to be imported in application.css once we use the new ITCSS structure in other places, too.
import 'src/stylesheets/it.scss'

import {
  cookieValueForProject,
  hasWirWunderCookie,
  transformToWirwunderHost,
} from '../shared/helpers/wirwunder_helpers'
import { About } from './about'
import { ClientBannerList } from './client_banner_list'
import { CreateFundraisingEventCta } from './create_fundraising_event_cta'
import { Description } from './description'
import { DonateButton } from './donate_button'
import { DonationsList } from './donations_list'
import { Engagement } from './engagement'
import { Header } from './header'
import { NeedList } from './need_list'
import { NewsTeaserList } from './news_teaser_list'
import { OrganisationProjectSummary } from './organisation_project_summary'
import styles from './ppp.module.css'
import { Progress } from './progress'
import { ProjectInfo } from './project_info'
import { PublicFaceInfo } from './public_face_info'
import { QuickAccessBox as PPPQuickAccessBox } from './quick_access_box'
import { Section } from './section'
import { SharingContainer } from './sharing_container'
import { SubscribeCta } from './subscribe_cta'
import { MatchingEventWithTrackingData, PPPProject, PPPProps, SharedDonateButtonProps } from './types'
import { VideoPlayer } from 'src/modules'
import {
  CtaBar,
  CtaButtonGroup,
  Gallery,
  GtmHelper,
  LogoSpinner,
  SharingGridButton,
  TaxNotice,
  report,
  themeColor,
  useJsonApi,
} from 'src/shared'
import analytics from 'product-analytics'
import classNames from 'classnames'
import { isArray } from 'betterplace-utils'

export const ProjectPageLoader = ({ url }: { url: string }) => {
  const { data, loading } = useJsonApi<PPPProps>(url)

  if (loading)
    return (
      <div className={styles.loadingContainer}>
        <LogoSpinner color={themeColor('green-500')} />
      </div>
    )

  if (data) return <ProjectPage {...data} />

  //Todo: Discuss what error message we want to show here
  return null
}

const useSharedDonateButtonProps = (project: PPPProject, donateUrl: string): SharedDonateButtonProps => {
  const props = useMemo(
    () => ({
      status: project.status,
      donateUrl: donateUrl,
      projectId: project.id,
      projectTitle: project.title,
      carrierId: project.carrier?.id,
      carrierName: project.carrier?.name || '?',
      carrierBlocked: project.carrier?.blocked,
      clientName: project.clientUTMName,
    }),
    [
      project.status,
      project.id,
      project.title,
      project.carrier?.id,
      project.carrier?.name,
      project.carrier?.blocked,
      project.clientUTMName,
      donateUrl,
    ]
  )
  return props
}

const useGtmProjectImpressions = (project: PPPProject) => {
  useEffect(() => {
    GtmHelper.pushProductDetailImpression(project)
  }, [project])
}

const useClientBannerAnalytics = (clientName?: string) => {
  useEffect(() => {
    if (!clientName) return

    analytics.clientBannerShown({ client_name: clientName })
  }, [clientName])
}

const useMatchingEventAnalytics = (matchingEvents?: MatchingEventWithTrackingData[] | null) => {
  useEffect(() => {
    if (!matchingEvents?.length) return

    matchingEvents.forEach((matchingEvent) =>
      analytics.activeMatchingEvent({
        id: String(matchingEvent.id),
        event_type: matchingEvent.trackingEventType,
        name: matchingEvent.trackingName,
        actor: matchingEvent.trackingActor,
      })
    )
  }, [matchingEvents])
}

const getShareUrl = (originalShareUrl: string, projectId: string) => {
  const hasCookie = hasWirWunderCookie(projectId)
  if (!hasCookie) return originalShareUrl

  const wwHostUrl = transformToWirwunderHost(originalShareUrl)
  return wwHostUrl
}

const ProjectPage = (props: PPPProps) => {
  useGtmProjectImpressions(props.project)
  const trackingPageTitle = `${props.project.title} - ${props.project.carrier.name} - betterplace.org`
  const projectId = String(props.project.id)
  const cookieValue = cookieValueForProject(projectId)
  const hasNussbaumCookie = cookieValue === 'nussbaum'

  const { data: matchingEvents } = useJsonApi<Array<MatchingEventWithTrackingData>>(
    `${props.links.matchingEventsUrl}?is_nussbaum_version=${hasNussbaumCookie ? 'true' : 'false'}`
  )

  if (matchingEvents && !isArray(matchingEvents)) {
    report('matchingEventsUrl should return an array instead received', {
      matchingEvents: JSON.stringify(matchingEvents),
      link: props.links.matchingEventsUrl,
    })
  }

  useClientBannerAnalytics(props.project.clientName)
  useMatchingEventAnalytics(matchingEvents)

  const isNotDonatable = props.project.status !== 'activated' || props.project.carrier?.blocked

  const sharedDonateButtonProps = useSharedDonateButtonProps(props.project, props.links.donateUrl)

  const onNewsLinkClicked: React.MouseEventHandler<HTMLAnchorElement> = useCallback((event) => {
    event.preventDefault()
    const newsListElement = document.querySelector('#ppp-news-list')
    if (newsListElement instanceof HTMLElement) {
      newsListElement.scrollIntoView({ behavior: 'smooth' })
      setTimeout(() => newsListElement.focus(), 500)
    }
  }, [])

  const trackMainShareButtonClicked = useCallback(() => {
    analytics.shareButtonClicked({
      id: projectId,
      page_type: 'project',
      title: trackingPageTitle,
      button_location: 'ppp_main',
    })
  }, [projectId, trackingPageTitle])

  const trackStickyShareButtonClicked = useCallback(() => {
    analytics.shareButtonClicked({
      id: projectId,
      page_type: 'project',
      title: trackingPageTitle,
      button_location: 'ppp_sticky',
    })
  }, [projectId, trackingPageTitle])

  const shareUrl = getShareUrl(props.links.projectShareUrl, projectId)

  return (
    <>
      <div className={styles.hero}>
        <CtaBar sticky hasStatusMessage={isNotDonatable}>
          <CtaButtonGroup
            {...sharedDonateButtonProps}
            shareUrl={shareUrl}
            hasStatusMessage={isNotDonatable}
            utmMedium="ppp_sticky"
            utmContent={props.project.clientUTMName}
            onClickShareButton={trackStickyShareButtonClicked}
            trackingPageTitle={trackingPageTitle}
            trackingId={projectId}
          />
        </CtaBar>

        {props.project.managedByCurrentUser ? (
          <aside className={classNames(styles.adminBox, 'container')}>
            <PPPQuickAccessBox project={props.project} {...props.links} />
          </aside>
        ) : null}

        <Header
          projectTitle={props.project.title}
          projectStatus={props.project.status}
          profilePicture={props.project.profilePicture}
          gallery={
            <Gallery
              profilePicture={props.project.profilePicture}
              apiUrl={props.links.picturesApiUrl}
              additionalPicturesCount={props.project.picturesCount}
            />
          }
          videoEmbedUrl={props.links.videoEmbedUrl}
        />

        <Section>
          <OrganisationProjectSummary
            carrierProfilePictureUrl={props.project.carrier?.profilePicture.url}
            carrierUrl={props.links.carrierUrl}
            carrierName={props.project.carrier?.name || '?'}
            projectCity={props.project.city}
            projectCountry={props.project.country}
            shortDescription={props.project.summary}
          />
        </Section>
      </div>
      <div className={styles.main}>
        <Section background="gentle" backgroundMobileOnly={true}>
          {/* TODO: extract this out as a layout component */}
          <div className="flex flex-col-reverse desktop:flex-row gap-6">
            <div className="desktop:w-1/2">
              <DonationsList
                apiUrl={props.links.opinionsApiUrl}
                donationsCount={props.project.donationsCount}
                wwBranchName={props.project.wwBranchName}
              />
            </div>
            <div className="desktop:w-1/2" data-testid="ppp-progress-donate">
              <Progress
                progressPercentage={props.project.progressPercentage}
                donatedAmountInCents={props.project.donatedAmountInCents}
                targetAmountInCents={props.project.targetAmountInCents}
                donationsCount={props.project.donationsCount}
                link={
                  props.project.newsCount > 0 && (
                    <a href="#ppp-news-list" onClick={onNewsLinkClicked}>
                      {i18n.t('projects.show.stats.updates')}
                    </a>
                  )
                }
              />
              {props.project.status === 'activated' && (
                <div className={styles.taxNotice}>
                  <TaxNotice highlightStart={true} />
                </div>
              )}
              <div className={styles.buttonGroup}>
                <DonateButton buttonLocation="PPP Main" size="large" block {...sharedDonateButtonProps} />
                <SharingGridButton
                  shareUrl={shareUrl}
                  platforms={['email', 'facebook', 'twitter', 'link', 'whatsapp', 'messenger']}
                  modalHeader={i18n.t('projects.show.stats.share_modal_headline')}
                  buttonLabel={i18n.t('projects.show.stats.share_button_label')}
                  block
                  size="large"
                  utmMedium="ppp_stats"
                  utmContent={props.project.clientUTMName}
                  onClickShareButton={trackMainShareButtonClicked}
                  trackingId={projectId}
                  trackingPageTitle={trackingPageTitle}
                />
              </div>
            </div>
          </div>
        </Section>

        <Section spacing="large">
          <ProjectInfo
            media={
              props.links.videoEmbedUrl && (
                <div className={styles.aboutProjectGridItem}>
                  <div id="video-player" tabIndex={-1}>
                    <VideoPlayer videoUrl={props.links.videoEmbedUrl} />
                  </div>
                </div>
              )
            }
            content={<Description description={props.project.description} />}
            sidebar={
              <PublicFaceInfo
                publicFacePictureUrl={props.project.publicFace.profilePicture.url}
                publicFaceDisplayName={props.project.publicFace.displayName}
                carrierName={props.project.carrier?.name || '?'}
                newMessageUrl={props.links.newMessageUrl}
              />
            }
          />
        </Section>

        <Section spacing="large">
          <NeedList
            apiUrl={props.links.needsApiUrl}
            completedApiUrl={props.links.completedNeedsApiUrl}
            projectApiUrl={props.links.projectApiUrl}
          />
        </Section>

        <Section spacing="large" background="gentle" backgroundMobileOnly={true}>
          <Engagement
            allowFundraisingEvents={props.project.allowFundraisingEvents}
            subscribeCta={
              <SubscribeCta projectTitle={props.project.title || ''} url={props.links.newsletterSubscriptionUrl} />
            }
            createFundraisingEventCta={
              <CreateFundraisingEventCta
                ctaUrl={props.links.createFundraisingEventUrl}
                readmoreUrl={props.links.fundraisingEventLandingpageUrl}
              />
            }
          />
        </Section>

        <Section spacing="large" id="ppp-news-list">
          <NewsTeaserList blogsApiUrl={props.links.latestBlogsApiUrl} newsListUrl={props.links.newsListUrl} />
        </Section>

        <Section spacing="large" background="prominent" fullWidth={true}>
          <SharingContainer shareUrl={shareUrl} trackingId={projectId} trackingPageTitle={trackingPageTitle} />
        </Section>

        {props.project.clientBannerList.length > 0 && (
          <Section spacing="large">
            <ClientBannerList clientBannerList={props.project.clientBannerList} />
          </Section>
        )}

        <Section spacing="large" background="foggy" fullWidth={true}>
          <About />
        </Section>
      </div>
    </>
  )
}

export { getShareUrl }
